import { Component, ElementRef, ViewChild, signal, input, output } from '@angular/core';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { Activity, Content, TrackXAPIPayload, ContentScoringMethod } from '@mira-avant/mtlibs';
import { ReportingService } from '../../../../../services/reporting.service';
import { TakeService } from '../../../../../services/take.service';
import { ActivatedRoute } from '@angular/router';
import { H5P } from 'h5p-standalone';
import { H5pService } from '../../../../../services/h5p.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CommonModule } from '@angular/common';

interface H5PChildResult {
	id: string;
	raw: number;
	max: number;
	duration: number;
}

@Component({
	selector: 'app-h5p-player',
	standalone: true,
	imports: [CommonModule, ZorroModule],
	templateUrl: './h5p-player.component.html',
	styleUrl: './h5p-player.component.scss',
})
export class H5pPlayerComponent {
	@ViewChild('container') public container: ElementRef | undefined;
	activity = input<Activity | undefined>();
	content = input<Content | undefined>();
	onReady = output<boolean>();
	started = signal<boolean>(false);
	loading = signal<boolean>(true);
	dontTrackTake = input<boolean>(false);

	private takeNumber: number | undefined;
	private h5pResults: H5PChildResult[] = [];

	constructor(
		public reportingService: ReportingService,
		public takeService: TakeService,
		public route: ActivatedRoute,
		private h5pService: H5pService,
		private zorroMessenger: NzMessageService
	) {}

	async ngOnInit(): Promise<void> {
		const act = this.activity();
		const cont = this.content();

		if (!act || !cont) {
			throw new Error('Missing activity or content');
		}

		if (this.dontTrackTake()) {
			this.loading.set(false);
			return;
		}

		if (
			[ContentScoringMethod.PERCENTAGE, ContentScoringMethod.PASS_FAIL, ContentScoringMethod.VIEWED].includes(
				cont.scoring_method as ContentScoringMethod
			)
		) {
			await this.takeService
				.createTake$({
					course_uuid: this.route.snapshot.params['courseUuid'],
					location_uuid: this.route.snapshot.params['locationUuid'],
					section_uuid: this.route.snapshot.params['sectionUuid'],
					unit_uuid: this.route.snapshot.params['unitUuid'],
					lesson_uuid: this.route.snapshot.params['lessonUuid'],
					activity_uuid: act.uuid,
					content_uuid: cont.uuid,
					scoring_method: cont.scoring_method as ContentScoringMethod,
				})
				.then(async takeResponse => {
					if (!act || !cont) {
						throw new Error('Missing activity or content');
					}

					this.loading.set(false);
					this.takeNumber = takeResponse.take.take;

					if (
						[ContentScoringMethod.PASS_FAIL, ContentScoringMethod.VIEWED].includes(
							cont.scoring_method as ContentScoringMethod
						)
					) {
						await this.takeService.completeTake$({
							course_uuid: this.route.snapshot.params['courseUuid'],
							location_uuid: this.route.snapshot.params['locationUuid'],
							section_uuid: this.route.snapshot.params['sectionUuid'],
							unit_uuid: this.route.snapshot.params['unitUuid'],
							lesson_uuid: this.route.snapshot.params['lessonUuid'],
							activity_uuid: act.uuid,
							content_uuid: cont.uuid,
							take: this.takeNumber,
							duration: 0,
						});
					}
				})
				.catch(error => {
					this.zorroMessenger.error('Unable to begin activity. Please try again later.');
				});
		}
	}

	public async activate(): Promise<void> {
		const act = this.activity();
		if (!act) {
			throw new Error('Missing activity');
		}

		this.started.set(true);
		this.h5pService.loading(true);

		try {
			const cont = this.content();
			let h5pUrl: string | null | undefined = cont?.active_text?.content_uri;

			if (!h5pUrl) {
				console.error('No H5P URL found for activity', act);
				throw new Error('Missing h5pUrl');
			}

			// const subDomain: string = UrlHelper.getLeftMostSubDomains();

			// h5pUrl =
			// 	h5pUrl.indexOf('https://prod.') > -1
			// 		? h5pUrl.replace('https://prod.', `https://${subDomain}.`)
			// 		: h5pUrl;
			const urlContainsZip = h5pUrl.includes('.zip');

			h5pUrl = urlContainsZip
				? this.rewriteUrlToCurrentDomain(h5pUrl.replace('.zip', '').replace('.h5p', ''))
				: this.rewriteUrlToCurrentDomain(h5pUrl.replace('/h5p.json', ''));

			const options = {
				h5pJsonPath: h5pUrl,
				frameJs: '/assets/h5p/h5p-standalone-3.5.0/frame.bundle.js',
				frameCss: '/assets/h5p/h5p-standalone-3.5.0/styles/h5p.css',
				customCss: '/assets/customH5p.css',
				fullScreen: false,
			};

			await this.waitForContainer();

			if (!this.container) {
				throw new Error('cannot find container 1');
			}

			await new H5P(this.container.nativeElement, options)
				.then(() => this.waitForIframeWindow())
				.then(() => this.waitForExternalDispatcher())
				.then(async () => {
					// Emit via signal output instead of EventEmitter
					this.onReady.emit(true);
				})
				.then(() => this.connectReporting());
		} finally {
			this.h5pService.loading(false);
		}
	}

	private rewriteUrlToCurrentDomain(originalUrl: string): string {
		try {
			const currentUrl = window.location.href;
			const originalUrlObj = new URL(originalUrl);
			const currentUrlObj = new URL(currentUrl);
			const currentHost = currentUrlObj.hostname;

			if (currentHost !== 'localhost' && !currentHost.includes('localhost')) {
				originalUrlObj.hostname = currentHost;
			}

			return originalUrlObj.toString();
		} catch (error) {
			console.error('Error rewriting URL:', error);
			return originalUrl;
		}
	}

	private async connectReporting(): Promise<void> {
		if (!this.container) {
			throw new Error('cannot find container 2');
		}

		const iframe = this.container.nativeElement.querySelector('iframe');
		if (!iframe) {
			throw new Error('cannot find iframe');
		}

		const iframeWindow = iframe.contentWindow;
		if (!iframeWindow) {
			throw new Error('cannot find iframeWindow');
		}

		iframeWindow.H5P.externalDispatcher.on('xAPI', async (event: any) => {
			if (this.dontTrackTake()) {
				console.warn('Skipping xAPI event processing because dontTrackTake is true');
				return;
			}

			const cont = this.content();
			const act = this.activity();

			if (!cont?.uuid || !this.takeNumber || !act) {
				throw new Error('Missing content uuid or take number');
			}

			console.info('xAPI event', event);

			const eventResult = this.reportingService.processXApiEvent(event);

			const xApiPayload: Omit<TrackXAPIPayload, 'student_cognito_uuid' | 'client_uuid'> = {
				course_uuid: this.route.snapshot.params['courseUuid'],
				location_uuid: this.route.snapshot.params['locationUuid'],
				section_uuid: this.route.snapshot.params['sectionUuid'],
				unit_uuid: this.route.snapshot.params['unitUuid'],
				lesson_uuid: this.route.snapshot.params['lessonUuid'],
				activity_uuid: act.uuid,
				content_uuid: cont.uuid,
				take: this.takeNumber,
				xapi: event.data,
				scoring_method: cont.scoring_method as ContentScoringMethod,
			};

			await this.reportingService.trackXapi$(xApiPayload);

			if (!cont?.scoring_method || cont.scoring_method !== ContentScoringMethod.PERCENTAGE) {
				console.warn('Skipping reporting & XAPI events as this content is not using percentage scoring.');
				return;
			}

			if (eventResult.completed && eventResult.score) {
				const subContentId =
					event.data.statement?.object?.definition?.extensions?.['http://h5p.org/x-api/h5p-subContentId'];

				if (subContentId) {
					const existingIndex = this.h5pResults.findIndex(result => result.id === subContentId);

					if (existingIndex !== -1) {
						this.h5pResults[existingIndex] = {
							id: subContentId,
							raw: eventResult.score.raw,
							max: eventResult.score.max,
							duration: eventResult.duration || 0,
						};
					} else {
						this.h5pResults.push({
							id: subContentId,
							raw: eventResult.score.raw,
							max: eventResult.score.max,
							duration: eventResult.duration || 0,
						});
					}

					const totalRaw = this.h5pResults.reduce((sum, result) => sum + result.raw, 0);
					const totalMax = this.h5pResults.reduce((sum, result) => sum + result.max, 0);
					const totalDuration = this.h5pResults.reduce((sum, result) => sum + result.duration, 0);

					await this.takeService.completeTake$({
						course_uuid: this.route.snapshot.params['courseUuid'],
						location_uuid: this.route.snapshot.params['locationUuid'],
						section_uuid: this.route.snapshot.params['sectionUuid'],
						unit_uuid: this.route.snapshot.params['unitUuid'],
						lesson_uuid: this.route.snapshot.params['lessonUuid'],
						activity_uuid: act.uuid,
						content_uuid: cont.uuid,
						take: this.takeNumber,
						duration: totalDuration,
						questions_correct: totalRaw,
						questions_total: totalMax,
					});
				} else {
					await this.takeService.completeTake$({
						course_uuid: this.route.snapshot.params['courseUuid'],
						location_uuid: this.route.snapshot.params['locationUuid'],
						section_uuid: this.route.snapshot.params['sectionUuid'],
						unit_uuid: this.route.snapshot.params['unitUuid'],
						lesson_uuid: this.route.snapshot.params['lessonUuid'],
						activity_uuid: act.uuid,
						content_uuid: cont.uuid,
						take: this.takeNumber,
						duration: eventResult.duration || 0,
						questions_correct: eventResult.score.raw,
						questions_total: eventResult.score.max,
					});
				}
			}
		});
	}

	private waitForExternalDispatcher(): Promise<void> {
		return new Promise<void>(resolve => {
			let interval = setInterval(() => {
				if (this.container?.nativeElement.querySelector('iframe')?.contentWindow?.H5P?.externalDispatcher) {
					clearInterval(interval);
					resolve();
				}
			}, 100);
		});
	}

	private waitForIframeWindow(): Promise<void> {
		return new Promise<void>(resolve => {
			let interval = setInterval(() => {
				if (this.container?.nativeElement.querySelector('iframe')?.contentWindow) {
					clearInterval(interval);
					resolve();
				}
			}, 100);
		});
	}

	private async waitForContainer(): Promise<void> {
		return new Promise<void>(resolve => {
			let interval = setInterval(() => {
				if (this.container) {
					clearInterval(interval);
					resolve();
				}
			}, 100);
		});
	}

	ngOnDestroy(): void {
		// No subscriptions to clean up anymore since we're using takeNumber directly
	}
}
